/**
 * Sekcja `browser` payloadu `POST /front-api/trx-api/www/update-payway` (PEP-1293).
 *
 * Po co: transakcja kartowa (kanał 1500) idzie przez 3DS, a serwer katalogowy oczekuje w żądaniu
 * uwierzytelnienia opisu przeglądarki płatnika — pola odpowiadają parametrom `browserJavaEnabled`,
 * `browserLanguage`, `browserColorDepth`, `browserScreenHeight`, `browserScreenWidth`, `browserTZ`
 * z EMV 3DS. Front jest jedynym miejscem, które je zna: backend widzi wyłącznie nagłówki HTTP,
 * więc bez tej sekcji bank dostaje uboższy zestaw danych do oceny ryzyka i częściej wystawia
 * challenge zamiast przepuścić transakcję bez tarcia.
 *
 * ⚠️ Sekcja dołączana jest WYŁĄCZNIE dla kanałów z `CARD_ID_LIST` (dziś: 1500) — patrz
 * `sendPaymentUpdateTrx` w `src/base/store/modules/payment.js`. Kanały portfelowe (Google Pay,
 * Apple Pay) mają własne uwierzytelnienie po stronie dostawcy i tej sekcji nie dostają.
 *
 * ⚠️ Nic tutaj nie może rzucić. Kod biegnie na ścieżce płatności, synchronicznie, przed `post()`:
 * wyjątek wyleciałby z `dispatch('sendPaymentUpdateTrx')` **synchronicznie**, czyli poza `.catch()`
 * pięciu wołających i zostawiłby użytkownika na kręciołku bez widoku błędu. Dlatego każdy odczyt
 * ma wartość zapasową, a niedostępne API daje `null`, nie wyjątek.
 *
 * → `doc/dokumentacja/platnosci.md`, „Sekcja `browser` w `/update-payway`"
 */

/**
 * Metoda podająca język przeglądarki w formacie payloadu (dwie wielkie litery, np. `PL`).
 *
 * ⚠️ To język **przeglądarki**, nie język bramki — cała sekcja opisuje klienta, nie transakcję.
 * `navigator.language` oddaje `pl-PL`, kontrakt chce `PL`, stąd obcięcie (ten sam zabieg co
 * w `setPaymentLanguage`).
 *
 * @returns {string|null}
 */
function getBrowserLanguage() {
  const language = navigator?.language
  if (typeof language !== 'string' || !language) {
    return null
  }
  return language.substring(0, 2).toUpperCase()
}

/**
 * Metoda sprawdzająca obsługę apletów Javy.
 *
 * `navigator.javaEnabled()` jest przestarzałe i w każdej dzisiejszej przeglądarce oddaje `false`,
 * ale kontrakt 3DS wymaga pola. W części webview metody nie ma wcale, więc sprawdzamy typ.
 *
 * @returns {boolean}
 */
function isJavaEnabled() {
  try {
    return typeof navigator?.javaEnabled === 'function' ? !!navigator.javaEnabled() : false
  } catch {
    return false
  }
}

/**
 * Metoda podająca różnicę między czasem UTC a lokalnym w minutach, jako łańcuch.
 *
 * ⚠️ Znak jest taki, jaki oddaje `getTimezoneOffset()` (dla Polski `-60` zimą i `-120` latem),
 * czyli odwrotny do potocznego „UTC+1" — tak samo definiuje `browserTZ` EMV 3DS. Nie „poprawiaj"
 * go na dodatni.
 *
 * @returns {string}
 */
function getTimeZoneOffset() {
  return String(new Date().getTimezoneOffset())
}

/**
 * Metoda budująca sekcję `browser` payloadu.
 *
 * @returns {{
 *   userAgent: string|null,
 *   language: string|null,
 *   screenHeight: number|null,
 *   screenWidth: number|null,
 *   screenColorDepth: number|null,
 *   timeZone: string,
 *   javaEnabled: boolean,
 *   javaScriptEnabled: boolean,
 * }}
 */
export function getBrowserData() {
  const screen = window?.screen

  return {
    userAgent: navigator?.userAgent ?? null,
    language: getBrowserLanguage(),
    screenHeight: screen?.height ?? null,
    screenWidth: screen?.width ?? null,
    screenColorDepth: screen?.colorDepth ?? null,
    timeZone: getTimeZoneOffset(),
    javaEnabled: isJavaEnabled(),
    // Stała: kod, który buduje ten payload, wykonuje się w przeglądarce z włączonym JavaScriptem.
    javaScriptEnabled: true,
  }
}
